/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { LayerManager } from "abreact/incubator/ui/manager/LayerManager";
import { AbPopupContext, LayerGroupContext } from "abreact/incubator/ui/popup/PopupContext";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { useLayoutOnMount } from "abreact/utils/AbHooks";
import * as React from "react";
import * as ReactDOM from "react-dom";

/**
 * The properties for a popup portal.
 */

// A note about the provided close function. It is important that it is always invoked exactly once, no matter how
// the popup is removed. There are 3 ways, and they do the following:
// 1. The AbPopup is removed from the dom. This triggers the cleanup function in AbPopupPortal's unmount effect, which
//    invokes the popup handle's close function, which removes the layer, triggering unmount.
// 2. The handle has close invoked on it. This also triggers the cleanup function mentioned in 1. However, this doesn't
//    lead to a second invocation due to the blocking if statements in the handle close function.
// 3. Something removes the layer from the dom. This calls the popup's .ab-popup-portal div ref function with null. If
//    the portal hasn't already begun to unmount, the provided close function will be invoked.
//
// The three methods cover the ways the popup can be closed, and if statements ensure that there won't be duplicate invocations.

/**
 * A portal that can display an inline popup in the layer manager.
 *
 * Note: this component takes its children out of the render tree
 * so they will not be rerendered as normal.
 */
export const AbPopupPortal = props => {
  const [portalContainer, setPortalContainer] = React.useState(null);
  const willUnmount = React.useRef(false);
  const layerGroupId = React.useContext(LayerGroupContext);
  const popupHandle = React.useRef(null);
  useLayoutOnMount(() => {
    popupHandle.current = PopupUtils.showPopup(/*#__PURE__*/React.createElement("div", {
      className: "ab-popup-portal",
      ref: element => {
        if (!willUnmount.current) {
          setPortalContainer(element);
          if (!element) {
            willUnmount.current = true;
            popupHandle.current?.close();
            props.close?.();
          }
        }
      },
      "data-test-component": "ab-popup-portal"
    }), {
      id: props.popupId,
      layerGroupId: layerGroupId ?? LayerManager.DEFAULT_GROUP_ID,
      isDeclarative: true,
      ownerPopupRegistry: props.ownerPopupRegistry
    });
    return () => {
      if (!willUnmount.current) {
        willUnmount.current = true;
        popupHandle.current?.close();
        props.close?.();
      }
    };
  });
  if (!popupHandle.current || !portalContainer) {
    return null;
  }
  return /*#__PURE__*/ReactDOM.createPortal(/*#__PURE__*/React.createElement(AbPopupContext.Provider, {
    value: {
      popupHandle: popupHandle.current
    }
  }, /*#__PURE__*/React.createElement(LayerGroupContext.Provider, {
    value: popupHandle.current.layerGroupId
  }, props.children)), portalContainer);
};
//# sourceMappingURL=AbPopupPortal.js.map
